import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import UserLayout from '../components/UserLayout';
import Header from '../components/Header';
import { apiClient, getPublicDashboardSummary } from '../api/api';
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  AlertCircle,
  ExternalLink,
  ChevronRight,
  Download,
  Zap,
  Package,
  LayoutGrid,
  Heart,
  Megaphone,
  Snowflake,
  Gift,
  Star,
  Clock,
  Calendar,
  X,
  ShoppingBag
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Loader from '../components/Loader';
import CRMWidget from '../components/dashboard/CRMWidget';
import './DashboardPage.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isPreviewUser } from '../utils/previewAccess';
import InfoTip from '../components/InfoTip';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardPage = ({ isPublic = false }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const authContext = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  // New SPS colour bands + info icons are visible to preview accounts only (see utils/previewAccess.js).
  const previewUser = !isPublic && isPreviewUser(authContext.user);

  // Use auth context only if not public, otherwise handle locally
  const selectedShopId = !isPublic ? authContext.selectedShopId : null;
  const globalDateRange = !isPublic ? authContext.dateRange : 'Last 7 Days';
  const setGlobalDateRange = !isPublic ? authContext.setDateRange : null;

  const [localDateRange, setLocalDateRange] = useState('Last 7 Days');
  const dateRange = isPublic ? localDateRange : globalDateRange;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSkusModal, setShowSkusModal] = useState(false);

  // Helper to get order metrics based on selected date range
  const getOrderMetrics = () => {
    if (!data?.Order) return {};
    return data.Order[dateRange] || {};
  };

  const orderMetrics = getOrderMetrics();

  useEffect(() => {
    if (!isPublic && !selectedShopId) {
      setLoading(false);
      return;
    }
    fetchDashboardSummary();
  }, [selectedShopId, dateRange]);

  const fetchDashboardSummary = async () => {
    try {
      setLoading(true);
      if (isPublic && token) {
        const output = await getPublicDashboardSummary(token, dateRange);
        setData(output.data);
      } else {
        let url = `/dashboard-summary?range=${dateRange}`;
        if (selectedShopId) {
          url += `&shop_id=${selectedShopId}`;
        }
        const output = await apiClient.get(url);
        setData(output.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      // Preview links are now gated behind a real login (2026-09-17) -- the backend returns a
      // real 401 (not logged in at all) or 403 (logged in, but not authorized for this
      // specific shop) instead of ever silently showing data to an unauthenticated visitor.
      if (isPublic && error.response?.status === 401) {
        navigate(`/?return_to=${encodeURIComponent(`/shared/${token}`)}`);
        return;
      }
      if (isPublic && error.response?.status === 403) {
        setError("You're logged in, but this login doesn't have access to this shop's dashboard.");
      } else {
        setError('Failed to load data. Please try refreshing the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    const n = parseFloat(num);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(isNaN(n) ? 0 : n);
  };

  const getScoreDetails = (score) => {
    if (score === null || score === undefined) return { color: '#666', label: 'Data Unavailable', border: 'rgba(255, 255, 255, 0.1)' };
    // New bands (Setu, 2026-09-27): 4.0+ green, 3.5-3.99 yellow, under 3.5 red. Preview accounts only.
    const isGreen = previewUser ? score >= 4.0 : score > 4.1;
    const isYellow = previewUser ? score >= 3.5 : score >= 3.6;
    if (isGreen) return { color: '#10b981', label: 'Healthy Status', border: 'rgba(16, 185, 129, 0.2)' };
    if (isYellow) return { color: '#eab308', label: 'Medium or caution range', border: 'rgba(234, 179, 8, 0.2)' };
    return { color: '#ef4444', label: 'Low or high-risk score', border: 'rgba(239, 68, 68, 0.2)' };
  };

  const scoreDetails = getScoreDetails(data?.health?.sps);

  // Reused in original code, now updated for new CSS structure if needed, or inline for specific widget
  const MetricCard = ({ label, sub, value, growth, icon: Icon, color, loading }) => {
      const isPositive = (growth || '').includes('+');
      const isNegative = (growth || '').includes('-');
      // Only negative when an explicit '-' is present — N/A, 0%, empty all render neutral
      const badgeClass = !isPositive && !isNegative ? 'neutral' : (isPositive ? 'positive' : 'negative');

      return (
        <div className={`metric-card ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-top">
                <span className="card-label">{label}</span>
                <span className={`growth-badge ${badgeClass}`}>
                    {growth?.includes('+') ? '↑' : (growth?.includes('-') ? '↓' : '')} {growth?.replace('+','').replace('-','')}
                </span>
            </div>
            <div className="card-value">{value}</div>
            <div className="card-sub">{sub}</div>
        </div>
      );
  };

  const DashboardContent = (
    <div className="page-relative-wrapper">
      {error && (
        <div style={{background:'#2d1a1a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'12px 16px',borderRadius:'8px',marginBottom:'16px'}}>
          {error}
        </div>
      )}
      <div className="dashboard-container">

        {/* Row 1: AI Banner and Health */}
        <div className="top-row-grid">
          
          {/* ShopAI Weekly Recap Widget */}
          <div className={`ai-recap-card ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="recap-header">
              <div className="recap-header-left">
                <div className="recap-icon-box">
                  <div className="inner-icon-bg">
                    <ShoppingBag size={22} color="#ff85b3" />
                  </div>
                </div>
                <div>
                  <div className="recap-title">{data?.ai_recap?.title || 'ShopAI Weekly Recap'}</div>
                  <div className="recap-subtitle">{data?.ai_recap?.date || 'Weekly Insights'}</div>
                </div>
              </div>
              {/* View Details removed — no backing page */}
            </div>
            <div className="recap-body">
              {(data?.ai_recap?.insights || []).map((ins, i) => (
                <p key={i} className="recap-insight">
                  <span className="insight-prefix" style={{ color: ins.color }}>{ins.prefix}</span>
                  {ins.text}
                </p>
              ))}
            </div>
          </div>

          {/* Shop Health Widget */}
          <div className={`shop-health-card ${loading ? 'shimmer skeleton' : ''}`} style={{ borderColor: scoreDetails.border }}>
            <div className="health-header">
                <div className="health-title" style={{ color: scoreDetails.color }}>
                    <Heart size={20} color={scoreDetails.color} fill={scoreDetails.color} /> Shop Health
                </div>
                <div className="health-status-badge" style={{ backgroundColor: `${scoreDetails.color}20`, color: scoreDetails.color, borderColor: `${scoreDetails.color}40` }}>
                    {scoreDetails.label}
                </div>
            </div>

            <div className="sps-row">
                <div className="sps-label">SPS Score</div>
                <div className="sps-value" style={{ color: scoreDetails.color }}>
                    {data?.health?.sps !== null && data?.health?.sps !== undefined ? data.health.sps.toFixed(1) : 'N/A'}<span className="sps-max" style={{ color: scoreDetails.color }}>/5</span>
                </div>
            </div>

            <div className="health-summary-grid">
                <div className="health-summary-card">
                    <div className="summary-label">Unread Violations</div>
                    <div className="summary-value" style={{fontSize: '20px'}}>{data?.health?.active_violations || 0}</div>
                </div>
                <div className="health-summary-card">
                    <div className="summary-label">Policy Warnings</div>
                    <div className="summary-value" style={{fontSize: '20px'}}>{data?.health?.policy_warnings || 0}</div>
                </div>
                {/* Cancel Rate card removed — same field as Seller-Fault Cancellation Rate row below */}
                {data?.health?.fulfillment_sla && data.health.fulfillment_sla !== 'N/A' && data.health.fulfillment_sla !== '--' && (
                    <div className="health-summary-card">
                        <div className="summary-label">Fulfillment SLA</div>
                        <div className="summary-value" style={{color: '#10b981', fontSize: '20px'}}>{data.health.fulfillment_sla}</div>
                    </div>
                )}
            </div>

            <div className="health-details-grid">
                {/* Left Column: Operational Metrics — from Shop Performance Score page */}
                <div>
                     <div className="detail-row">
                        <span className="detail-label">Return Rate <span style={{fontSize:'10px',color:'#9CA3AF'}}>(60-day)</span></span>
                        <span className="detail-value">{data?.health?.return_rate || 'N/A'}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">Late Dispatch Rate</span>
                        <span className="detail-value">{data?.health?.late_dispatch_rate || 'N/A'}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">Seller-Fault Cancellation <span style={{fontSize:'10px',color:'#9CA3AF'}}>(30-day)</span></span>
                        <span className="detail-value">{data?.health?.seller_fault_cancellation_rate || 'N/A'}</span>
                     </div>
                     {data?.health?.avg_response_time && data.health.avg_response_time !== 'N/A' && (
                     <div className="detail-row">
                        <span className="detail-label">After-Sales Handling Time <span style={{fontSize:'10px',color:'#9CA3AF'}}>(60-day)</span></span>
                        <span className="detail-value">{data.health.avg_response_time.replace(/(\d)([a-zA-Z])/, '$1 $2')}</span>
                     </div>
                     )}
                     <div className="detail-row">
                        <span className="detail-label">Negative Review Rate <span style={{fontSize:'10px',color:'#9CA3AF'}}>(60-day)</span></span>
                        <span className="detail-value">{data?.health?.negative_reviews || 'N/A'}</span>
                     </div>
                     {data?.health?.im_dissatisfaction_rate && data.health.im_dissatisfaction_rate !== 'N/A' && (
                     <div className="detail-row">
                        <span className="detail-label">IM Dissatisfaction <span style={{fontSize:'10px',color:'#9CA3AF'}}>(60-day)</span></span>
                        <span className="detail-value">{data.health.im_dissatisfaction_rate}</span>
                     </div>
                     )}
                </div>

                {/* Right Column: Order Volumes — time range matches selected date range above */}
                <div>
                    <div className="detail-row">
                        <span className="detail-label">Shipped <span style={{fontSize:'10px',color:'#9CA3AF'}}>({dateRange})</span></span>
                        <span className="detail-value">{data?.health?.shipped || 0}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">Completed <span style={{fontSize:'10px',color:'#9CA3AF'}}>({dateRange})</span></span>
                        <span className="detail-value">{data?.health?.completed || 0}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">Pending</span>
                        <span className="detail-value">{data?.health?.pending || 0}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">To Ship</span>
                        <span className="detail-value">{data?.health?.to_ship || 0}</span>
                     </div>
                     <div className="detail-row">
                        <span className="detail-label">Cancelled <span style={{fontSize:'10px',color:'#9CA3AF'}}>({dateRange})</span></span>
                        <span className="detail-value">{data?.health?.cancelled || 0}</span>
                     </div>
                     {/* Returns & Refunds management counts */}
                     {(data?.health?.returns_awaiting_action > 0 || data?.health?.returns_auto_approved_7d > 0) && (<>
                       <div className="detail-row" style={{marginTop:'8px',paddingTop:'8px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                         <span className="detail-label">Returns Awaiting Action</span>
                         <span className="detail-value" style={{color: data?.health?.returns_awaiting_action > 0 ? '#f97316' : 'inherit'}}>{data?.health?.returns_awaiting_action || 0}</span>
                       </div>
                       <div className="detail-row">
                         <span className="detail-label">Auto-Approved <span style={{fontSize:'10px',color:'#9CA3AF'}}>(Last 7d)</span></span>
                         <span className="detail-value">{data?.health?.returns_auto_approved_7d || 0}</span>
                       </div>
                       {data?.health?.returns_can_appeal > 0 && (
                         <div className="detail-row">
                           <span className="detail-label">Can Be Appealed</span>
                           <span className="detail-value">{data?.health?.returns_can_appeal || 0}</span>
                         </div>
                       )}
                     </>)}
                </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Widget */}
        <div className="metrics-section">
            <div className="metrics-header">
                <div className="metrics-title">
                    <LayoutGrid size={20} /> Key Metrics
                </div>
                <button className="export-btn" onClick={() => {
                    if (!data) return;
                    const km = data.key_metrics || {};
                    const shopName = (data.shop_name || 'shop').replace(/\s+/g, '-');
                    const rows = [
                        ['Metric', 'Value'],
                        ['GMV', km.gmv || 0],
                        ['Orders', km.orders || 0],
                        ['AOV', km.aov || 0],
                        ['Customers', km.customers || 0],
                        ['Refunds', km.refunds || 0],
                        ['GMV Growth', km.gmv_growth || ''],
                        ['Orders Growth', km.orders_growth || ''],
                    ];
                    // Ad Spend/Revenue/ROAS/CPO and P&L stay out of the public export even
                    // though Ads is now shown on-page there too (2026-09-15) — export is a
                    // different privacy tier than the on-screen summary (2026-09-10 fix: export
                    // was leaking non-public data).
                    if (!isPublic) {
                        const ads = data.ads || {};
                        const pnl = data.pnl || {};
                        rows.push(
                            ['', ''],
                            ['Ad Spend', ads.spend || 0],
                            ['Ad Revenue', ads.revenue || 0],
                            ['ROAS', ads.roas || 0],
                            ['Ad Orders', ads.orders || 0],
                            ['CPO', ads.cpo || 0],
                            ['', ''],
                            ['P&L Revenue', pnl.revenue || 0],
                            ['P&L Profit', pnl.profit || 0],
                            ['P&L Growth', pnl.growth || ''],
                        );
                    }
                    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `shopOS-${shopName}-${dateRange.replace(/\s+/g,'-')}-${new Date().toISOString().slice(0,10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                }} disabled={loading || !data}>
                    <Download size={14} /> Export
                </button>
            </div>

            <div className="metrics-grid">
                {/* FIXED (2026-09-01, real bug reported by Setu: "Yesterday time window
                    doesn't show GMV block"): this card used to hide itself completely
                    whenever the value was exactly 0, so it couldn't tell a genuinely
                    zero-order day apart from a period that just hasn't synced yet -- both
                    looked identical once reduced to a bare number. The backend now reports
                    key_metrics.gmv_synced explicitly (based on whether the scraper actually
                    wrote this range's data, not on the value). Show the real number
                    (including a real $0.00) whenever it's synced, and a clear "Syncing"
                    state only when it genuinely isn't -- never vanish the card outright. */}
                <MetricCard
                    label="GMV"
                    sub={data?.key_metrics?.gmv_synced === false ? 'Syncing…' : 'Gross Merchandise Value'}
                    value={data?.key_metrics?.gmv_synced === false ? '—' : formatCurrency(data?.key_metrics?.gmv || 0)}
                    growth={data?.key_metrics?.gmv_synced === false ? '' : (data?.key_metrics?.gmv_growth || '0%')}
                    icon={DollarSign}
                    loading={loading}
                />
                <MetricCard 
                    label="Orders" 
                    sub="Total Orders" 
                    value={(data?.key_metrics?.orders || 0).toLocaleString()} 
                    growth={data?.key_metrics?.orders_growth || '0%'} 
                    icon={ShoppingCart} 
                    loading={loading}
                />
                <MetricCard 
                    label="AOV" 
                    sub="Average Order Value" 
                    value={formatCurrency(data?.key_metrics?.aov)} 
                    growth={data?.key_metrics?.aov_growth || '0%'} 
                    icon={TrendingUp} 
                    loading={loading}
                />
                <MetricCard 
                    label="Customers" 
                    sub="Shop Customers" 
                    value={(data?.key_metrics?.customers || 0).toLocaleString()} 
                    growth={data?.key_metrics?.customers_growth || '0%'} 
                    icon={Users} 
                    loading={loading}
                />
                <MetricCard
                    label="Pending Returns"
                    sub="Awaiting Action"
                    value={data?.health?.returns_awaiting_action !== undefined
                        ? (data.health.returns_awaiting_action).toLocaleString()
                        : '--'}
                    growth=""
                    icon={AlertCircle}
                    loading={loading}
                />
            </div>

            <div className="charts-row">
                {/* Gross Revenue Chart */}
                <div className={`chart-container ${loading ? 'shimmer skeleton' : ''}`}>
                    <div className="chart-header">
                        <div className="chart-title">Gross Revenue</div>
                        <div className="chart-legend">
                             <div className="legend-dot" style={{background: '#10b981'}}></div> {dateRange}
                        </div>
                    </div>
                    <div style={{ height: '200px' }}>
                        <Line 
                            data={{
                            labels: data?.trends?.labels || [],
                            datasets: [{
                                data: data?.trends?.revenue || [],
                                borderColor: '#10b981',
                                backgroundColor: (context) => {
                                    const ctx = context.chart.ctx;
                                    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                                    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
                                    gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                                    return gradient;
                                },
                                fill: true,
                                tension: 0.4,
                                pointRadius: 0,
                                pointHoverRadius: 4,
                            }]
                            }}
                            options={{ 
                                responsive: true, 
                                maintainAspectRatio: false, 
                                plugins: { legend: { display: false } }, 
                                scales: { 
                                    x: { 
                                        display: true, 
                                        grid: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)' },
                                        ticks: { color: '#666', font: { size: 10 } }
                                    }, 
                                    y: { 
                                        display: true, 
                                        grid: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)' },
                                        ticks: { 
                                            color: '#666', 
                                            font: { size: 10 },
                                            callback: (value) => '$' + value
                                        },
                                        beginAtZero: true
                                    } 
                                } 
                            }}
                        />
                    </div>
                </div>

                {/* Orders Chart */}
                <div className={`chart-container ${loading ? 'shimmer skeleton' : ''}`}>
                    <div className="chart-header">
                        <div className="chart-title">Orders</div>
                        <div className="chart-legend">
                             <div className="legend-dot" style={{background: '#3b82f6'}}></div> {dateRange}
                        </div>
                    </div>
                    <div style={{ height: '200px' }}>
                         <Bar 
                            data={{
                            labels: data?.trends?.labels || [],
                            datasets: [{
                                data: data?.trends?.orders || [],
                                backgroundColor: (context) => {
                                    const ctx = context.chart.ctx;
                                    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                                    gradient.addColorStop(0, '#3b82f6');
                                    gradient.addColorStop(1, '#1d4ed8'); // darker blue
                                    return gradient;
                                },
                                borderRadius: 4,
                                barThickness: 24
                            }]
                            }}
                            options={{ 
                                responsive: true, 
                                maintainAspectRatio: false, 
                                plugins: { legend: { display: false } }, 
                                scales: { 
                                    x: { 
                                        display: true, 
                                        grid: { display: false },
                                        ticks: { color: '#666', font: { size: 10 } }
                                    }, 
                                    y: { 
                                        display: true, 
                                        grid: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)' },
                                        ticks: { color: '#666', font: { size: 10 } },
                                        beginAtZero: true
                                    } 
                                } 
                            }}
                         />
                </div>
            </div>
        </div>
    </div>

        {/* Row 4: P&L, Active Campaigns, CRM, Sample Tracker, Paid Ads (2-col grid, auto-flows
            to a 3rd row) */}
        <div className="quad-grid">
           {/* P&L Widget — hidden 2026-08-24 per Setu's review: "let's just need to
               turn this off for now." Net Profit was already hidden pending a
               formula review; Setu asked for the whole card off until the numbers
               are trustworthy. Re-enable once resolved (see git history for the
               Net Profit formula note). */}
           {false && <div className="dashboard-card-wrapper">
               <div className={`dashboard-card ${loading ? 'shimmer skeleton' : ''} ${!loading && (!data?.pnl?.revenue || data.pnl.revenue === 0) ? 'blurred-widget' : ''}`}>
                  <div className="metrics-header">
                      <div className="metrics-title"><TrendingUp size={20} color="#10b981" /> P&L Overview</div>
                  </div>
                  <div className="pnl-header-row">
                     <div style={{color: '#888', fontSize:'13px'}}>Gross Revenue</div>
                     <div className="pnl-gross-rev">
                         <span style={{
                            color: (data?.pnl?.growth || '').includes('+') ? '#10b981' : (data?.pnl?.growth || '').includes('-') ? '#ef4444' : '#888',
                            fontSize:'11px',
                            fontWeight:'600',
                            marginRight:'8px'
                         }}>
                            {data?.pnl?.growth || 'N/A'}
                         </span>
                         <span style={{fontSize:'18px', fontWeight:'700'}}>{formatCurrency(data?.pnl?.revenue)}</span>
                     </div>
                  </div>
                  
                  {/* Simple Chart Area */}
                  <div className="pnl-chart-area">
                      <Line 
                        data={{
                            labels: data?.trends?.labels || [],
                            datasets: [{
                                data: data?.trends?.revenue || [],
                                borderColor: '#10b981',
                                backgroundColor: (context) => {
                                    const ctx = context.chart.ctx;
                                    const gradient = ctx.createLinearGradient(0, 0, 0, 100);
                                    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
                                    gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                                    return gradient;
                                },
                                fill: true,
                                tension: 0.4,
                                pointRadius: 0
                            }]
                        }}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: { x: { display: false }, y: { display: false } },
                            layout: { padding: 0 }
                        }}
                      />
                  </div>
    
                  <div className="pnl-details-grid">
                      {(data?.pnl?.details || []).map((item, idx) => (
                          <div key={idx} className="pnl-row">
                              <span>{item.label}</span>
                              <span className="pnl-row-cost">{formatCurrency(item.value)}</span>
                          </div>
                      ))}
                  </div>
    
                  {/* Net Profit hidden 2026-08-23 pending a formula review with Setu/management:
                      commission is calculated against TikTok's "Creator-attributed GMV" (which
                      overlaps/double-counts across creators per TikTok's own attribution model)
                      but subtracted directly from the shop's real, non-overlapping Gross Revenue,
                      producing misleadingly negative Net Profit even when the business is healthy.
                      Gross Revenue and Ad Spend above are unaffected and still accurate. Re-enable
                      once the formula is reconciled. */}
               </div>

               {!loading && (!data?.pnl?.revenue || data.pnl.revenue === 0) && (
                 <div className="auth-overlay">
                    <div className="auth-overlay-message">
                      Authorize your shop to unlock P&L data. <br/>
                      Go to <Link to="/myshops" style={{ color: '#10b981', fontWeight: '600' }}>My Shops</Link> → click <strong>Authorize</strong>.
                    </div>
                 </div>
               )}
           </div>}

             {/* Active Promotion Widget */}
             <div className={`dashboard-card ${loading ? 'shimmer skeleton' : ''}`}>
                <div className="metrics-header" style={{ marginBottom: '16px' }}>
                   <div className="metrics-title"><Megaphone size={20} color="#f97316" /> Active Promotion</div>
                </div>
               
                <div className="hidden-scrollbar" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                    {/* Ongoing Section */}
                    {(data?.campaigns?.active_promotions || []).filter(p => p.status === 'Ongoing').map((promo, idx) => (
                        <div key={`ongoing-${idx}`} className="campaign-box highlighted" style={{
                            marginBottom: '16px',
                            padding: '16px',
                            background: 'rgba(16, 185, 129, 0.05)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                        }}>
                            <div className="campaign-header" style={{ marginBottom: '12px' }}>
                                <div>
                                    <div className="campaign-title" style={{ fontSize: '15px', fontWeight: '700', color: isLight ? '#111827' : '#fff' }}>{promo.name}</div>
                                    <div className="campaign-type" style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>{promo.type}</div>
                                </div>
                                <div className="status-badge" style={{
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    background: 'rgba(16, 185, 129, 1)',
                                    color: '#fff',
                                    fontSize: '10px',
                                    fontWeight: '700'
                                }}>
                                    {promo.status}
                                </div>
                            </div>
                            {promo.gmv && (
                                <div style={{ fontSize: '12px', color: '#10b981', fontWeight: '600', marginBottom: '6px' }}>
                                    GMV: {promo.gmv}
                                </div>
                            )}
                            <div style={{ fontSize: '11px', color: isLight ? '#6B7280' : '#aaa', display: 'flex', justifyContent: 'space-between' }}>
                                <span>Start: {promo.start_time}</span>
                                <span>End: {promo.end_time}</span>
                            </div>
                        </div>
                    ))}

                    {/* Promotion GMV Section — only show when value is a real dollar amount (has decimal) */}
                    {data?.promotion?.promotion_gmv?.toString().includes('.') && (
                        <div className="promotion-gmv-section">
                            <div className="promotion-gmv-label">
                                Promotion GMV
                                {previewUser && (
                                    <InfoTip text={`Showing ${dateRange === 'Last 28-30 Days' ? 'Last 28 days' : dateRange}, the same range selected at the top. TikTok reports Promotion GMV for Yesterday, Last 7 days and Last 28 days only, so this section is not shown for Today.`} />
                                )}
                            </div>
                            <div className="promotion-gmv-value-row">
                                <div className="promotion-gmv-value">
                                    {formatCurrency(data.promotion.promotion_gmv)}
                                </div>
                                <div className="promotion-gmv-percentage">
                                    {data.promotion.promotion_percentage && <span style={{ fontSize: '10px' }}>{data.promotion.promotion_percentage.toString().includes('-') ? '▼' : '▲'}</span>} {data.promotion.promotion_percentage || '—'}
                                </div>
                            </div>
                            <div className="promotion-gmv-bar" />
                            <div className="promotion-gmv-contribution">
                                {data.promotion.contribution?.replace(/(\d+\.\d{2,})%/, (_, n) => `${parseFloat(n).toFixed(1)}%`)}
                            </div>
                        </div>
                    )}

                    {/* Separator Line */}
                    {((data?.campaigns?.active_promotions || []).some(p => p.status === 'Ongoing') || data?.promotion?.promotion_gmv?.toString().includes('.')) && (
                        <div style={{ height: '1px', background: isLight ? '#E5E7EB' : '#1a1a1a', margin: '20px 0' }} />
                    )}

                    {/* Upcoming Section */}
                    {(data?.campaigns?.active_promotions || []).filter(p => p.status === 'Upcoming').map((promo, idx) => (
                        <div key={`upcoming-${idx}`} className="promo-row" style={{
                            padding: '12px 0',
                            borderBottom: isLight ? '1px solid #E5E7EB' : '1px solid #0a0a0a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: '500', color: isLight ? '#111827' : '#eee' }}>{promo.name}</div>
                                <div style={{ fontSize: '11px', color: isLight ? '#6B7280' : '#666' }}>{promo.type} • {promo.start_time}</div>
                            </div>
                            <div style={{ fontSize: '10px', color: isLight ? '#9CA3AF' : '#888', fontWeight: '600' }}>{promo.status}</div>
                        </div>
                    ))}
                    
                    {(!data?.campaigns?.active_promotions || data?.campaigns?.active_promotions.length === 0) && (
                        <div style={{ color: '#666', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                            No active promotions found
                        </div>
                    )}
                </div>

                {data?.campaigns?.ending_alert && (
                   <div className="camp-reminder" style={{ marginTop: 'auto', borderTop: isLight ? '1px solid #E5E7EB' : '1px solid #1a1a1a', paddingTop: '12px' }}>
                       <AlertCircle size={14} color="#f97316" />
                       <span>{data.campaigns.ending_alert}</span>
                   </div>
                )}
            </div>

           {/* CRM Widget (New) */}
           <CRMWidget data={data?.crm} loading={loading} style={{ height:'450px' }} />

            {/* Sample Tracker Widget */}
            <div className={`dashboard-card ${loading ? 'shimmer skeleton' : ''}`} style={{ height: '450px' }}>
              <div className="metrics-header">
                  <div className="metrics-title">
                      <Package size={20} color="#3b82f6" /> Sample Tracker
                      {previewUser && (
                          <InfoTip text={dateRange === 'Last 7 Days' || dateRange === 'Last 28-30 Days'
                              ? `Showing ${dateRange === 'Last 28-30 Days' ? 'Last 28 days' : 'Last 7 days'}, the same range selected at the top. Sample data is only collected for Last 7 days and Last 28 days.`
                              : `Sample data is only collected for Last 7 days and Last 28 days, so there are no sample figures for ${dateRange}. The zeros here mean no data, not zero samples. Switch to Last 7 Days or Last 28-30 Days to see them.`} />
                      )}
                  </div>
              </div>
              {/* Conversion period label — this is a fixed window, NOT rolling 7 days */}
              {data?.samples?.conversion_period && (
                <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '10px', marginTop: '-4px' }}>
                  Funnel period: {data.samples.conversion_period}
                </div>
              )}

              <div className="sample-grid">
                  <div className="sample-box">
                      <div className="sample-label">Requested</div>
                      <div className="sample-val">{data?.samples?.total_pipeline || 0}</div>
                  </div>
                  <div className="sample-box">
                      <div className="sample-label">Approved</div>
                      <div className="sample-val green">{data?.samples?.approved || 0}</div>
                  </div>
                  <div className="sample-box">
                      <div className="sample-label">Shipped</div>
                      <div className="sample-val">{data?.samples?.shipped || 0}</div>
                  </div>
                  <div className="sample-box">
                      <div className="sample-label">Posted</div>
                      <div className="sample-val purple">{data?.samples?.posted || 0}</div>
                  </div>
              </div>

              {/* Product SKU Performance Section */}
              {data?.samples?.top_skus && data.samples.top_skus.length > 0 && (
                  <div className="top-skus-section hidden-scrollbar" style={{
                      marginTop: '16px',
                      marginBottom: '16px',
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      paddingRight: '8px'
                  }}>
                      <div className="sku-perf-header" style={{ fontSize: '13px', fontWeight: '600', color: '#10b981', marginBottom: '4px', position: 'sticky', top: 0, zIndex: 1, padding: '4px 0' }}>Product SKU Performance</div>
                      {data.samples.top_skus.map((sku, idx) => (
                          <div key={idx} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '16px',
                              background: 'rgba(16, 185, 129, 0.03)',
                              borderRadius: '16px',
                              border: '1px solid rgba(16, 185, 129, 0.15)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              marginBottom: '12px'
                          }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                 <div style={{ fontSize: '14px', fontWeight: '700', color: isLight ? '#111827' : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }} title={sku.name}>
                                     {sku.name}
                                 </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                     <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '500' }}>
                                        Product SKU
                                     </div>
                                     <div style={{ display: 'flex', gap: '8px', fontSize: '10px', color: isLight ? '#9CA3AF' : 'rgba(255,255,255,0.5)' }}>
                                        <span>Req: <span style={{color: isLight ? '#374151' : '#fff'}}>{sku.samples_requested || 0}</span></span>
                                        <span>Ship: <span style={{color: isLight ? '#374151' : '#fff'}}>{sku.samples_shipped || 0}</span></span>
                                     </div>
                                  </div>
                              </div>
                              <div style={{ 
                                  padding: '4px 12px', 
                                  borderRadius: '20px', 
                                  background: '#10b981', 
                                  color: '#fff', 
                                  fontSize: '11px', 
                                  fontWeight: '800',
                                  flexShrink: 0 
                              }}>
                                  {sku.gmv}
                              </div>
                          </div>
                      ))}
                  </div>
              )}

              <div style={{marginTop:'auto'}}>
                  <div className="pipeline-row" style={{marginBottom:0}}>
                      <span>Sales Generated</span>
                      <div className="pipeline-val">
                          {/* This is a COUNT of affiliates who generated sales from
                              samples, not a dollar amount -- confirmed via Setu's
                              review ("should just be 44... 44 affiliates have pulled
                              in money from their video, rather than $44"). Was
                              wrongly wrapped in formatCurrency(). "videos" label
                              added 2026-09-11 per Setu's Slack follow-up: a bare
                              number here read as ambiguous. */}
                          {data?.samples?.sales_generated ?? 0}
                          <span style={{ fontSize: '11px', fontWeight: '400', color: isLight ? '#9CA3AF' : 'rgba(255,255,255,0.5)', marginLeft: '4px' }}>videos</span>
                          {(() => {
                            const sg = data?.samples?.sales_growth;
                            // Only show badge when there is meaningful sales growth data
                            if (!sg || sg === 'N/A' || sg === '+0%' || sg === '+0.0%') return null;
                            return (
                              <span style={{
                                color: sg.includes('-') ? '#ef4444' : sg.includes('+') ? '#10b981' : '#888',
                                fontSize:'11px',
                                display:'flex',
                                alignItems:'center'
                              }}>
                                {sg.includes('+') ? '↑' : sg.includes('-') ? '↓' : ''}
                                {sg.replace('+','').replace('-','')}
                              </span>
                            );
                          })()}
                      </div>
                  </div>
                  <div className="pipeline-row" style={{marginTop:'12px', marginBottom:0}}>
                      <span>Content Rate</span>
                      <div className="pipeline-val">
                          {data?.samples?.content_rate || '0%'}
                      </div>
                  </div>
              </div>

            {/* Top Affiliates Section — moved here (2026-09-10, per feedback) to sit
                next to Sample Tracker, filling the slot P&L leaves empty while hidden. */}
            <div className={`affiliates-card ${loading ? 'shimmer skeleton' : ''}`}>
                 <div className="metrics-header" style={{ marginBottom: '24px' }}>
                    <div className="metrics-title"><Users size={20} color="#a855f7" /> Top Affiliates</div>
                 </div>

                 <div className="affiliate-list">
                    {!loading && (data?.top_affiliates || []).length === 0 ? (
                       <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', gap: '8px', color: '#555' }}>
                          <Users size={28} color="#333" />
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#888' }}>No affiliate activity</div>
                          <div style={{ fontSize: '11px', color: '#555', textAlign: 'center' }}>No affiliate creators active for this period</div>
                       </div>
                    ) : (
                       (data?.top_affiliates || []).slice(0, 3).map((aff, i) => (
                          <div key={i} className="affiliate-row">
                             <div className={`rank-number rank-num-${i+1}`}>{i+1}</div>

                             <img
                               src={aff.avatar || ''}
                               alt={aff.name}
                               className="affiliate-avatar"
                               onError={e => { e.target.style.display = 'none'; }}
                             />

                             <div className="affiliate-info">
                                <div className="affiliate-name">{aff.name}</div>
                                <div className="affiliate-followers">
                                   <Users size={12} /> {aff.followers} followers
                                </div>
                             </div>

                             <div className="affiliate-gmv">
                                <div className={`gmv-value val-${i+1}`}>{formatCurrency(aff.gmv)}</div>
                                <div className="gmv-label">GMV</div>
                             </div>
                          </div>
                       ))
                    )}
                 </div>
            </div>
           </div>

            {/* Paid Ads Summary — a true sibling of Sample Tracker (not nested inside it),
                so it renders in its own grid cell to the right. Real scraped promotion data,
                same source as the standalone Paid Ads page. */}
            <div className={`ads-card ${loading ? 'shimmer skeleton' : ''}`}>
                 <div className="metrics-header" style={{ marginBottom: '20px' }}>
                    <div className="metrics-title"><Megaphone size={20} color="#3b82f6" /> Paid Ads Summary</div>
                 </div>
                 {data?.ads?.orders > data?.key_metrics?.orders && (
                    <div className="ads-quirk-note">
                        Ad Orders may show higher than Total Orders for the same period — a known TikTok display-timezone difference between the Ads dashboard and shop reporting, not a data error.
                    </div>
                 )}

                 <div className="ads-summary-row">
                    {[
                      { label: 'Ad Spend', val: formatCurrency(data?.ads?.spend), growth: data?.ads?.spend_growth || '', color: 'green', invertCpo: false },
                      { label: 'ROI', val: (data?.ads?.roi || 0).toFixed(2) + 'x', growth: data?.ads?.roi_growth || '', color: 'orange', invertCpo: false },
                      { label: 'Orders', val: data?.ads?.orders || 0, growth: data?.ads?.orders_growth || '', color: 'purple', invertCpo: false },
                      { label: 'Revenue', val: formatCurrency(data?.ads?.revenue), growth: data?.ads?.revenue_growth || '', color: 'blue', invertCpo: false },
                      { label: 'CPO', val: formatCurrency(data?.ads?.cpo), growth: data?.ads?.cpo_growth || '', color: 'red', invertCpo: true }
                    ].map((ad, i) => {
                      // CPO is inverted: rising cost = bad (red ↓), falling = good (green ↑)
                      const isPos = (ad.growth || '').includes('+');
                      const isNeg = (ad.growth || '').includes('-');
                      const growthClass = !isPos && !isNeg ? 'neutral'
                        : ad.invertCpo ? (isPos ? 'down' : 'up')
                        : (isPos ? 'up' : 'down');
                      const arrow = !isPos && !isNeg ? '' : ad.invertCpo ? (isPos ? '↓' : '↑') : (isPos ? '↑' : '↓');
                      return (
                      <div key={i} className={`ad-metric-box ${ad.color}`}>
                         <div className="ad-label">{ad.label}</div>
                         <div className="ad-value">{ad.val}</div>
                         <div className={`ad-growth ${growthClass}`}>
                             {arrow} {(ad.growth || '').replace('+','').replace('-','')}
                         </div>
                      </div>
                      );
                    })}
                 </div>

                 <div className="chart-container" style={{ border: 'none', padding: 0 }}>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#666', marginBottom: '16px' }}>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '3px', backgroundColor: '#10b981' }}></div> Ad Spend</div>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '12px', height: '3px', backgroundColor: '#3b82f6' }}></div> Revenue</div>
                    </div>
                    <div style={{ height: '180px' }}>
                         <Line
                            data={{
                                labels: data?.trends?.labels || [],
                                datasets: [
                                    {
                                        label: 'Ad Spend',
                                        data: data?.trends?.ad_spend || [],
                                        borderColor: '#10b981',
                                        borderWidth: 2,
                                        tension: 0.4,
                                        pointRadius: 0
                                    },
                                    {
                                        label: 'Ad Revenue',
                                        data: data?.trends?.ad_revenue || [],
                                        borderColor: '#3b82f6',
                                        borderWidth: 2,
                                        tension: 0.4,
                                        pointRadius: 0
                                    }
                                ]
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: {
                                    x: {
                                        display: true,
                                        grid: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)' },
                                        ticks: { color: '#666', font: { size: 10 } }
                                    },
                                    y: {
                                        display: true,
                                        grid: { color: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.05)' },
                                        ticks: { color: '#666', font: { size: 10 } }
                                    }
                                }
                            }}
                         />
                    </div>
                 </div>
            </div>
        </div>

        {/* Row 5: TikTok Shop Campaigns */}
        <div style={{ padding: '24px', backgroundColor: isLight ? '#FFFFFF' : '#050505', border: isLight ? '1px solid #E5E7EB' : '1px solid #1a1a1a', borderRadius: '24px', marginBottom: '32px', boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05),0 4px 16px rgba(0,0,0,0.04)' : 'none' }} className={loading ? 'shimmer skeleton' : ''}>
            <div className="campaigns-section-title"><Megaphone size={20} color="#f97316" /> Shop Campaigns</div>
            
            {/* Annual Campaigns Performance Section — hidden when no data scraped yet */}
            {(data?.tiktok_shop_campaigns?.annual_performance?.gmv ||
              data?.tiktok_shop_campaigns?.annual_performance?.orders ||
              data?.tiktok_shop_campaigns?.annual_performance?.items_sold) && (
              <>
                <div className="campaign-sub-header">Annual Campaigns Performance</div>
                <div className="annual-metrics-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                    marginBottom: '32px',
                    marginTop: '16px'
                }}>
                    <div className="annual-metric-box" style={{
                        padding: '16px',
                        backgroundColor: isLight ? '#F9FAFB' : '#0a0a0a',
                        borderRadius: '16px',
                        border: isLight ? '1px solid #E5E7EB' : '1px solid #1a1a1a',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: isLight ? '#9CA3AF' : '#888', fontSize: '12px', marginBottom: '8px' }}>GMV</div>
                        <div style={{ color: '#10b981', fontSize: '18px', fontWeight: '700' }}>
                            {formatCurrency(data?.tiktok_shop_campaigns?.annual_performance?.gmv)}
                        </div>
                    </div>
                    <div className="annual-metric-box" style={{
                        padding: '16px',
                        backgroundColor: isLight ? '#F9FAFB' : '#0a0a0a',
                        borderRadius: '16px',
                        border: isLight ? '1px solid #E5E7EB' : '1px solid #1a1a1a',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: isLight ? '#9CA3AF' : '#888', fontSize: '12px', marginBottom: '8px' }}>Orders</div>
                        <div style={{ color: '#3b82f6', fontSize: '18px', fontWeight: '700' }}>
                            {data?.tiktok_shop_campaigns?.annual_performance?.orders || 0}
                        </div>
                    </div>
                    <div className="annual-metric-box" style={{
                        padding: '16px',
                        backgroundColor: isLight ? '#F9FAFB' : '#0a0a0a',
                        borderRadius: '16px',
                        border: isLight ? '1px solid #E5E7EB' : '1px solid #1a1a1a',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: isLight ? '#9CA3AF' : '#888', fontSize: '12px', marginBottom: '8px' }}>Items Sold</div>
                        <div style={{ color: '#a855f7', fontSize: '18px', fontWeight: '700' }}>
                            {data?.tiktok_shop_campaigns?.annual_performance?.items_sold || 0}
                        </div>
                    </div>
                </div>
              </>
            )}

            {/* Enrolled Campaigns */}
            <div className="campaign-sub-header">Enrolled Campaigns</div>
            {(data?.tiktok_shop_campaigns?.enrolled || []).map((camp, idx) => (
                <div key={idx} className="shop-campaign-card">
                    <div className="enrolled-row">
                        <div className={`sc-icon ${camp.icon_color}`}>
                            {/* Simple icon mapping based on color/title */}
                            {camp.icon_color === 'purple' ? <Zap size={24} /> : <Gift size={24} />}
                        </div>
                        <div className="enrolled-info">
                            <div className="enrolled-title">{camp.title}</div>
                            <div className="enrolled-details">
                                <div>
                                    <div className="ed-col-label">Status</div>
                                    <div className="ed-col-val">{camp.status}</div>
                                </div>
                                <div>
                                    <div className="ed-col-label">Campaign Date</div>
                                    <div className="ed-col-val">{camp.date}</div>
                                </div>
                                <div>
                                    <div className="ed-col-label">Campaign Type</div>
                                    <div className="ed-col-val">{camp.type}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            {/* Available Campaigns -- CONFIRMED (2026-08-24, user screenshot): the
                section header rendered unconditionally even with zero campaigns,
                showing a bare "Available Campaigns" label with nothing under it.
                Setu's review: "remove if not easy to fix" for this case. Only the
                .map() below was ever conditional on data -- the header itself
                wasn't. Hide the whole section (header included) when there's
                nothing to show, same pattern as other empty-state hides tonight. */}
            {(data?.tiktok_shop_campaigns?.available || []).length > 0 && (
              <>
                <div className="campaign-sub-header" style={{ marginTop: '32px' }}>Available Campaigns</div>
                {data.tiktok_shop_campaigns.available.map((camp, idx) => (
                    <div key={idx} className="shop-campaign-card" style={{ padding: '16px 24px' }}>
                        <div className="available-row">
                             <div className={`sc-icon ${camp.icon_color}`} style={{ width: '40px', height: '40px', fontSize: '18px' }}>
                                {camp.icon_color === 'cyan' ? <Snowflake size={20} /> : (camp.icon_color === 'pink' ? <Heart size={20} /> : <Star size={20} />)}
                             </div>
                             <div className="avail-details">
                                 <div className="avail-title">{camp.title}</div>
                                 <div className={`campaign-tag tag-${camp.tag_color ? (camp.tag_color==='blue'?'blue':(camp.tag_color==='pink'?'pink':'yellow')) : 'blue'}`}>
                                     {camp.tag}
                                 </div>

                                 <div className="avail-meta">
                                     <div className="meta-item"><Calendar size={14} /> {camp.date}</div>
                                     <div className="meta-item" style={{ color: '#f59e0b' }}>
                                         <Clock size={14} />
                                         {camp.ends_in?.toLowerCase().includes('ends in')
                                            ? camp.ends_in
                                            : (camp.ends_in === 'N/A' ? 'N/A' : `Ends in ${camp.ends_in}`)}
                                     </div>
                                 </div>
                             </div>
                        </div>
                    </div>
                ))}
              </>
            )}
        </div>

        {/* Row 6: Products Table */}
        <div className={`sku-section ${loading ? 'shimmer skeleton' : ''}`}>
           <div className="sku-header">
              <div className="sku-title"><Package size={20} color={isLight ? '#374151' : '#fff'} /> Product SKUs <span style={{fontSize:'11px', fontWeight:400, color: isLight ? '#9CA3AF' : '#888', marginLeft:'6px'}}>(Last 28 Days)</span></div>
              <button className="view-all-btn" onClick={() => setShowSkusModal(true)}>View All</button>
           </div>
           
           <div className="sku-table-wrapper">
               <table className="sku-table">
                  <thead>
                     <tr>
                        <th style={{ width: '50%' }}>Product</th>
                        <th style={{ width: '30%' }}>Inventory</th>
                        <th style={{ width: '20%' }}>GMV</th>
                        {/* Orders column hidden — scraper selector broken, always returns 0. Re-enable once fixed. */}
                     </tr>
                  </thead>
                  <tbody>
                     {(data?.skus || []).slice(0, 4).map((sku, i) => {
                        const invValue = parseInt(String(sku.inventory || '0').split(' ')[0]);
                        const isLow = invValue < 15; // threshold matches Slack alert and Command Centre

                        return (
                           <tr key={i}>
                              <td>
                                 <div className="sku-info-cell">
                                     {sku.image_url ? (
                                        <img src={sku.image_url} alt={sku.name} className="sku-prod-img" />
                                     ) : (
                                        <div className="sku-img-placeholder-small" />
                                     )}
                                     <div>
                                        <div className="sku-name">{sku.name}</div>
                                        <div className="sku-code">SKU: {sku.sku}</div>
                                     </div>
                                  </div>
                              </td>
                               <td>
                                  <div className="center-cell-box">
                                     <div className={`inventory-pill ${isLow ? 'low' : 'ok'}`}>
                                        {isLow && <AlertCircle size={12} />}
                                        {sku.inventory}
                                     </div>
                                  </div>
                               </td>
                               <td className="gmv-cell">
                                  <div className="center-cell-box">{formatCurrency(sku.gmv)}</div>
                               </td>
                               {/* Orders cell hidden — see Orders column comment above */}
                           </tr>
                        );
                     })}
                  </tbody>
               </table>
           </div>
        </div>

        {/* Product SKUs Modal */}
        {showSkusModal && (
            <div className="modal-overlay" onClick={() => setShowSkusModal(false)}>
                <div className="sku-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <div className="modal-title">
                            <Package size={20} /> Product SKUs
                        </div>
                        <button className="modal-close" onClick={() => setShowSkusModal(false)}>
                            <X size={20} />
                        </button>
                    </div>

                    <div className="modal-body">
                        <div className="sku-table-wrapper">
                            <table className="sku-table modal-style">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50%' }}>Product</th>
                                        <th style={{ width: '30%' }}>Inventory</th>
                                        <th style={{ width: '20%' }}>GMV</th>
                                        {/* Orders column hidden — scraper selector broken, always returns 0. Re-enable once fixed. */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.skus || []).map((sku, i) => {
                                        const invValue = parseInt(String(sku.inventory || '0').split(' ')[0]);
                                        const isLow = invValue < 15; // threshold matches Slack alert and Command Centre

                                        return (
                                            <tr key={i}>
                                                <td>
                                                    <div className="sku-info-cell modal-layout">
                                                        {sku.image_url ? (
                                                            <img src={sku.image_url} alt={sku.name} className="sku-prod-img-large" />
                                                        ) : (
                                                            <div className="sku-img-placeholder" />
                                                        )}
                                                        <div>
                                                            <div className="sku-name">{sku.name}</div>
                                                            <div className="sku-code">SKU: {sku.sku}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="center-cell-box">
                                                        <div className={`inventory-pill ${isLow ? 'low' : 'ok'}`}>
                                                            {isLow && <AlertCircle size={12} />}
                                                            {sku.inventory}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="gmv-cell">
                                                    <div className="center-cell-box">{formatCurrency(sku.gmv)}</div>
                                                </td>
                                                {/* Orders cell hidden — see Orders column comment above */}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );

  if (!isPublic && !loading && !selectedShopId) {
    return (
      <UserLayout>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '60vh', color: '#9ca3af', gap: '16px', textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px' }}>🏪</div>
          <div style={{ fontSize: '20px', fontWeight: '600', color: isLight ? '#111827' : '#fff' }}>No shop connected</div>
          <div style={{ fontSize: '14px' }}>Go to My Shops to connect your shop account.</div>
          <Link to="/myshops" style={{
            marginTop: '8px', padding: '10px 24px', background: '#10b981',
            color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: '600'
          }}>
            Go to My Shops
          </Link>
        </div>
      </UserLayout>
    );
  }

  // Skeleton cards only kick in once the page is already mid-render with a theme applied;
  // on the very first load (no data yet at all) they read as blank white boxes rather than
  // a visible loading state, so show a real spinner instead for that initial gap.
  const initialLoad = loading && !data;

  if (isPublic) {
    return (
      <div className="public-dashboard-layout" style={{ backgroundColor: isLight ? '#F9FAFB' : '#000' }}>
        <Header
          isPublic={true}
          publicShopName={data?.shop_name || "Shared Dashboard"}
          dateRange={dateRange}
          setDateRange={setLocalDateRange}
        />
        <div className="public-content">
          {initialLoad ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
              <Loader size="large" />
            </div>
          ) : DashboardContent}
        </div>
      </div>
    );
  }

  return (
    <UserLayout>
      {initialLoad ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
          <Loader size="large" />
        </div>
      ) : DashboardContent}
    </UserLayout>
  );
};

export default DashboardPage;